import {
    useState,
    useMemo,
    useCallback,
} from 'react'

import AccordionItem, {
    AccordionItemType,
} from './ReorderableAccordionItem'
import usePersistedOrderState from './hooks/usePersistedOrderState'

type ReorderableAccordionListProps = {
    items: AccordionItemType[],
    initialHeightVh: number,
    persistentKey: string,
}

export default function ReorderableAccordionList({
    items: initialItems,
    initialHeightVh,
    persistentKey,
}: Readonly<ReorderableAccordionListProps>) {
    const [
        items,
        setItems,
    ] = useState(initialItems)

    const {
        onItemOrderChange,
        sortByItemOrderDelegate,
    } = usePersistedOrderState({
        persistentKey,
        initialItems,
    })

    const sortedItems = useMemo(() => {
        const newItems = [...items]

        return newItems.sort(sortByItemOrderDelegate)
    }, [
        items,
        sortByItemOrderDelegate,
    ])

    const moveItem = useCallback((index: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? index - 1 : index + 1

        if (newIndex < 0 || newIndex >= sortedItems.length) return

        const newItems = [...sortedItems];

        [
            newItems[index],
            newItems[newIndex],
        ] = [
            newItems[newIndex],
            newItems[index],
        ]

        onItemOrderChange(newItems)
        setItems(newItems)
    }, [
        sortedItems,
        onItemOrderChange,
    ])

    return (
        <>
            {sortedItems.map((item, index) => {
                return (
                    <AccordionItem
                        key={item.key}
                        item={item}
                        index={index}
                        first={index === 0}
                        last={index === items.length - 1}
                        moveItem={moveItem}
                        initialHeightVh={initialHeightVh}
                    />
                )
            })}
        </>
    )
}
